import { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  Fade,
  IconButton,
  InputAdornment,
  Paper
} from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid } from '@mui/x-data-grid';
import './keys.css';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import { format } from 'date-fns';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import { useNotification } from 'hook/useNotification';
import ApiKeyCustomToolbar from 'pages/apikeys/components/apiKeyCustomToolbar';
import ApiKeyDetails from 'pages/apikeys/components/apiKeysDetails';
import {
  useBulkDeleteApiKeysMutation,
  useCreateApiKeyMutation,
  useGetApiKeysQuery,
  useUpdateApiKeyMutation
} from 'services/apiKeyApi';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { parseDate, SIMPLE_DATE_FORMAT } from 'utils/formatTime';
import { copyToClipboard } from 'utils/stringUtils';

/**
 * Component for managing API keys, including displaying a list of API keys, adding, updating,
 * duplicating, and deleting keys. Uses a DataGrid for listing the keys and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <Keys />
 *
 * @returns {React.Component} The Keys management component.
 */
export default function Keys() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const { data: apiKeys, error: apiKeysError, isLoading: apiKeysLoading } = useGetApiKeysQuery();
  const [bulkDeleteApiKey] = useBulkDeleteApiKeysMutation();
  const [createApiKey] = useCreateApiKeyMutation();
  const [updateApiKey] = useUpdateApiKeyMutation();

  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);
  const [secureKey, setSecureKey] = useState(null);
  const [showSecureApiKey, setShowSecureApiKey] = useState(false);

  const processedApiKeys = (apiKeys || []).map((key) => ({
    ...key,
    creationDate: format(parseDate(key.creationDate), SIMPLE_DATE_FORMAT),
    lastModificationDate: format(parseDate(key.lastModificationDate), SIMPLE_DATE_FORMAT),
    expirationDate: calculateExpirationDate(key.creationDate, key.validityPeriod)
  }));

  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.keys.table.column.id` }),
      width: 50,
      disableColumnMenu: true
    },
    { field: 'displayableKey', headerName: msg({ id: `page.keys.table.column.key` }), width: 100 },
    {
      field: 'owner',
      headerName: msg({ id: `page.keys.table.column.owner` }),
      minWidth: 150,
      flex: 1
    },
    {
      field: 'validityPeriod',
      headerName: msg({ id: `page.keys.table.column.validityPeriod` }),
      width: 195
    },
    {
      field: 'creationDate',
      headerName: msg({ id: `page.keys.table.column.creationDate` }),
      width: 150
    },
    {
      field: 'expirationDate',
      headerName: msg({ id: `page.keys.table.column.expirationDate` }),
      width: 150
    },
    {
      field: 'description',
      headerName: msg({ id: `page.keys.table.column.description` }),
      width: 200,
      flex: 1
    },
    {
      field: 'status',
      headerName: msg({ id: `page.keys.table.column.status` }),
      minWidth: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          {params.value === 'ACTIVATED' ? (
            <DoneIcon fontSize="large" color="success" />
          ) : (
            <ErrorIcon fontSize="large" color="error" />
          )}
        </div>
      )
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelDialogClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCopyApiKeyDialogClose = () => {
    setSecureKey(null);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedApiKey(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedApiKey(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeleteApiKey(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.apikeys.delete.success' })
          });
          clearApiKeySelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.apikeys.delete.error' })
          });
        });
    }
  };

  const handleDuplicate = () => {
    if (selectedRows[0]) {
      setSelectedApiKey({ ...selectedRows[0], id: null, key: '' });
      setDetailDrawerOpen(true);
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const handleCopyApiKey = async () => {
    await copyToClipboard(secureKey);
    addNotification({
      type: 'success',
      title: msg({ id: 'notification.info.title' }),
      message: msg({ id: 'page.apikeys.copy.value.success' })
    });
  };

  const handleClickShowApiKey = () => setShowSecureApiKey((show) => !show);

  const selectedRows = rowSelectionModel.map((id) => apiKeys.find((key) => key.id === id));

  if (apiKeysError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.apikeys.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }} id="apiKeysContainer">
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            rows={processedApiKeys}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: apiKeys && apiKeys.length > 0 ? [apiKeys[0].id] : [] },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'owner', sort: 'asc' }]
              }
            }}
            slots={{ toolbar: ApiKeyCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                onDuplicate: handleDuplicate,
                selectedRows
              }
            }}
            loading={apiKeysLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <ApiKeyDetails
            apiKey={selectedApiKey}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelDialogClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelDialogClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
        <Dialog open={secureKey != null} onClose={handleCopyApiKeyDialogClose}>
          <DialogTitle>{msg({ id: 'dialog.copy.apikey.title' })}</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              <MDTypography
                display="flex"
                variant="body2"
                fontWeight="medium"
                color="error"
                sx={{ marginBottom: 2 }}
              >
                {msg({ id: 'dialog.copy.apikey.message' })}
              </MDTypography>
            </DialogContentText>
            <MDInput
              required
              label={msg({ id: 'field.apiKey.label' })}
              variant="filled"
              type={showSecureApiKey ? 'text' : 'password'}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                value: secureKey,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.copyApiKey' })}>
                      <IconButton onClick={handleCopyApiKey}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      aria-label={showSecureApiKey ? 'hide the password' : 'display the password'}
                      onClick={handleClickShowApiKey}
                    >
                      {showSecureApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCopyApiKey} color="primary">
              {msg({ id: 'button.copy' })}
            </MDButton>
            <MDButton onClick={handleCopyApiKeyDialogClose} color="secondary">
              {msg({ id: 'button.close' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function calculateExpirationDate(creationDate, validityPeriod) {
    const creation = parseDate(creationDate);
    creation.setDate(creation.getDate() + validityPeriod);
    return format(creation, SIMPLE_DATE_FORMAT);
  }

  function clearApiKeySelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedApiKey(null);
    setSelectedRowId(null);
  }

  function prepareApiKeyDataForSubmit(data) {
    return { ...data, roles: data?.roles.map((role) => role?.id) };
  }

  function handleCreate(data) {
    createApiKey({ ...data, roles: data.roles?.map((role) => role?.id) })
      .unwrap()
      .then((payload) => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.apikeys.create.success' })
        });
        clearApiKeySelected();
        setSecureKey(payload.secureApiKey);
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.apikeys.create.error' })
        });
      });
  }

  function handleUpdate(data) {
    updateApiKey({
      apiKeyId: data.id,
      apiKeyRequest: prepareApiKeyDataForSubmit(data)
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.apikeys.update.success' })
        });
        clearApiKeySelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.apikeys.update.error' })
        });
      });
  }
}
